import {
  CombineRuleHistory,
  CombineRuleHistorySearch,
  SearchCombineRuleHistoryResponseCombineRuleHistoryConnectionArgs,
} from '@/models/gen/graphql';
import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchCombineRuleHistoryDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchCombineRuleHistory.combineRuleHistoryConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Combine Rules History' } });

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof CombineRuleHistorySearch]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<CombineRuleHistory>>;
}

// SearchCombineRuleHistory Service
export const { searchCombineRuleHistory, refetchCombineRuleHistory, useSearchCombineRuleHistory } = ((): {
  searchCombineRuleHistory: ServiceMethod;
  refetchCombineRuleHistory: ServiceMethod;
  useSearchCombineRuleHistory: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchCombineRuleHistoryDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<CombineRuleHistory>> => {
        const emptyPayload = payload.every((value) => {
          return Object.values(value).every((v) => v === null);
        });
        const args: SearchCombineRuleHistoryResponseCombineRuleHistoryConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: !emptyPayload ? payload : [],
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchCombineRuleHistory: fetch,
    refetchCombineRuleHistory: refetch,
    useSearchCombineRuleHistory: useService,
  };
})();
